import React, { useEffect, useRef, useMemo } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { IonContent, IonPage } from '@ionic/react';
import { ApplicationState } from '../store';
import * as HeaderFooterStore from '../store/HeaderFooterStore';
import * as MenuItemDataStore from '../store/MenuItemStore';
import { RouteComponentProps, useLocation } from 'react-router';
import './MenuItem.scss';
import ModifierGroup from '../components/ModifierGroup';
import { baseUrl } from '../helpers/configHelper';
import HeaderDescription from '../components/HeaderDescription';
import DealHeader from '../components/DealHeader';
import { bindActionCreators } from 'redux';
import { showDealHeader, showModifierGroupsOnPages, shouldShowModifierGroup, getSelectedSkus } from '../helpers/menuHelpers';

interface MenuItemOwnProps {
    menuItemData: MenuItemDataStore.MenuItemDataState
    isBasket: boolean,
    isColleague: boolean
}

type MenuItemProps =
    & RouteComponentProps<{ categoryIdentifier: string }>
    & MenuItemOwnProps;

function MenuItem(props: MenuItemProps) {
    const dispatch = useDispatch();
    const location = useLocation();

    const menuItem = useSelector((state: ApplicationState) => state.menuItemData?.menuItem);
    const currentPage = useSelector((state: ApplicationState) => state.deal?.currentPage);

    const setTitle = bindActionCreators(HeaderFooterStore.actionCreators.setTitle, dispatch);
    const setBasketShowModal = bindActionCreators(HeaderFooterStore.actionCreators.setBasketShowModal, dispatch);

    const contentRef = useRef<HTMLIonContentElement>(null);

    const selectedSkus = useMemo(() => {
        if (!menuItem?.modifierGroups) {
            return [];
        }

        return getSelectedSkus(menuItem.modifierGroups);
    }, [menuItem?.modifierGroups])

    useEffect(() => {
        return () => {
            // Check if it's basket update, if so then dispatch SHOW_BASKET_MODAL
            if (props.isBasket) {
                setBasketShowModal(true);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!menuItem) {
            return;
        }

        const title = menuItem.menuItemName.replace(/\[\[.*\]\]/, '')
        setTitle(title, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuItem])

    useEffect(() => {
        contentRef.current?.scrollToTop();
    }, [contentRef]);

    useEffect(() => {
        contentRef.current?.scrollToTop();
    }, [contentRef, location.search]);

    return (
        <IonPage className="menuItemWrapper">
            <IonContent fullscreen ref={contentRef}>
                {menuItem &&
                    <>
                        {showDealHeader(menuItem, props.isColleague)
                            ?
                            <DealHeader
                                menuItem={menuItem}
                            />
                            :
                            <HeaderDescription
                                menuItem={menuItem}
                                headerImage={`${baseUrl()}/images/ProductShots/${menuItem.sku}_PDP.png`}
                                hideText={false}
                            />
                        }
                        {menuItem.modifierGroups
                            ?.filter((group: IMopModifierGroup) => {
                                if (!shouldShowModifierGroup(group, selectedSkus, menuItem.sku)) {
                                    return false;
                                }

                                if (!showModifierGroupsOnPages(menuItem)) {
                                    return true;
                                }

                                return group.showOnPage === currentPage;
                            })
                            .sort((a: IMopModifierGroup, b: IMopModifierGroup) => {
                                return a.showOnPage - b.showOnPage || a.order - b.order;
                            })
                            .map((modifierGroup: IMopModifierGroup, index: number) => {
                                return (
                                    <ModifierGroup
                                        key={'mg' + index}
                                        localId={index.toString()}
                                        modifierGroup={modifierGroup}
                                        isColleague={props.isColleague}
                                    />
                                )
                            })
                        }
                    </>
                }
            </IonContent>
        </IonPage>
    );
}

const mapStateToProps = (state: ApplicationState, ownProps: any) => ({
    menuItemData: state.menuItemData,
    isBasket: state.menuItemData?.isBasket,
    totalPrice: state.basket?.basket.totalPrice,
    isColleague: state.colleague?.isLoggedIn
});

const mapDispatchToProps = (
    dispatch: any) => {
    return {
        requestMenuItem: (menuItemId: number, pfIdentifier: string, referer: string | null, callback: any) => dispatch(MenuItemDataStore.actionCreators.requestMenuItem(menuItemId, pfIdentifier, "", callback)),
        setBasketShowModal: (value: boolean) => dispatch(HeaderFooterStore.actionCreators.setBasketShowModal(value)),
        setTitle: (value: string, showBack: boolean) => dispatch(HeaderFooterStore.actionCreators.setTitle(value, showBack))
    };
};

export default connect(
    mapStateToProps, // Selects which state properties are merged into the component's props
    mapDispatchToProps // Selects which action creators are merged into the component's props,
)(MenuItem as any);
